import React, { useState, useEffect } from 'react';
import { message, Card, Typography, Pagination, Spin } from 'antd';
import { EyeOutlined, MobileOutlined, EnvironmentOutlined, ClockCircleOutlined, WifiOutlined, DatabaseOutlined, AppstoreOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTable, useDrawer } from '@/hooks/index';
import { GlobalTable } from '@/index';
import type { TableColumn, TableAction } from '@/types/table';
import { devicesAPI } from '@/services/api/devices';
import type { DeviceListItem, DeviceDetail, UpgradeReport } from '@/types/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

/**
 * 升级记录组件
 */
interface UpgradeLogsProps {
  deviceId: number;
  deviceSn?: string;
}

const UpgradeLogs: React.FC<UpgradeLogsProps> = ({ deviceId, deviceSn }) => {
  const [upgradeLogs, setUpgradeLogs] = useState<UpgradeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // 获取升级日志
  useEffect(() => {
    const fetchUpgradeLogs = async () => {
      setLoading(true);
      try {
        const logs = await devicesAPI.getDeviceUpgradeLogs(deviceId);
        // 时间倒序排序
        const sortedLogs = [...logs].sort((a, b) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setUpgradeLogs(sortedLogs);
      } catch (error: any) {
        console.error('获取升级日志失败:', error);
        message.error('获取升级日志失败');
      } finally {
        setLoading(false);
      }
    };

    fetchUpgradeLogs();
  }, [deviceId]);

  // 计算当前页的数据
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = upgradeLogs.slice(startIndex, endIndex);
  const total = upgradeLogs.length;

  // 根据类型显示不同的状态和颜色
  const getTypeInfo = (type: string) => {
    switch (type) {
      case 'check':
        return { label: '检查更新', color: '#1890ff' };
      case 'download':
        return { label: '下载', color: '#52c41a' };
      case 'upgrade':
        return { label: '升级', color: '#52c41a' };
      default:
        return { label: type, color: '#8c8c8c' };
    }
  };

  return (
    <div className="space-y-4">
      {deviceSn && (
        <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
          <Text className="text-sm" style={{ color: 'var(--text-secondary)' }}>设备ID: </Text>
          <Text className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{deviceSn}</Text>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center" style={{ minHeight: '200px' }}>
          <Spin size="large" />
        </div>
      ) : upgradeLogs.length === 0 ? (
        <div className="bg-white rounded-lg p-4 text-center">
          <Text className="text-gray-500">暂无升级记录</Text>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {currentPageData.map((log) => {
              const typeInfo = getTypeInfo(log.type);
              const isSuccess = log.error_code === 0;
              const isUpgrade = log.type === 'upgrade';

              return (
                <div
                  key={log.id}
                  className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}
                      >
                        {typeInfo.label}
                      </span>
                      {isUpgrade && (
                        <span
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: isSuccess ? '#52c41a20' : '#ff4d4f20',
                            color: isSuccess ? '#52c41a' : '#ff4d4f',
                          }}
                        >
                          {isSuccess ? '成功' : '失败'}
                        </span>
                      )}
                    </div>
                    <Text className="text-gray-400 text-xs">
                      {dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Text className="text-gray-500 text-sm">当前版本:</Text>
                      <Text className="text-sm font-medium">{log.current_version || '-'}</Text>
                    </div>
                    <div className="flex items-center gap-2">
                      <Text className="text-gray-500 text-sm">目标版本:</Text>
                      <Text className="text-sm font-medium">{log.to_version || '-'}</Text>
                    </div>
                    {log.error_code !== 0 && log.error_msg && (
                      <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                        <Text className="text-red-600 text-xs">
                          错误码: {log.error_code} - {log.error_msg}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {total > pageSize && (
            <div className="flex justify-center mt-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total) => `共 ${total} 条记录`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * 设备详情组件
 */
interface DeviceDetailProps {
  deviceId: number;
}

const DeviceDetail: React.FC<DeviceDetailProps> = ({ deviceId }) => {
  const [deviceDetail, setDeviceDetail] = useState<DeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // 获取设备详情
  useEffect(() => {
    const fetchDeviceDetail = async () => {
      setLoading(true);
      try {
        const detail = await devicesAPI.getDeviceById(deviceId);
        setDeviceDetail(detail);
      } catch (error: any) {
        console.error('获取设备详情失败:', error);
        message.error('获取设备详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceDetail();
  }, [deviceId]);


  if (loading) {
    return <div className="p-6 text-center">加载中...</div>;
  }

  if (!deviceDetail) {
    return <div className="p-6 text-center text-gray-500">暂无设备详情</div>;
  }
  return (
    <div className="space-y-6">
      {/* 设备ID区域 */}
      <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
        <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-lg">
          <MobileOutlined className="text-2xl text-white" />
        </div>
        <div className="flex-1">
          <Text className="text-gray-500 text-sm block mb-1">设备ID</Text>
          <Title level={4} className="!mb-1">{deviceDetail.sn}</Title>
          <Text className="text-gray-600">{deviceDetail.model_name}</Text>
        </div>
      </div>

      {/* 基本信息 */}
      <div>
        <Title level={5} className="mb-4" style={{ color: '#f0f4f8' }}>基本信息</Title>
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <MobileOutlined className="text-xl text-blue-500" />
            <div className="flex-1">
              <Text className="text-gray-500 text-sm block">机型</Text>
              <Text className="text-base font-medium">{deviceDetail.model_name}</Text>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <EnvironmentOutlined className="text-xl text-green-500" />
            <div className="flex-1">
              <Text className="text-gray-500 text-sm block">地区</Text>
              <Text className="text-base font-medium">{deviceDetail.region}</Text>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <ClockCircleOutlined className="text-xl text-orange-500" />
            <div className="flex-1">
              <Text className="text-gray-500 text-sm block">最后检测时间</Text>
              <Text className="text-base font-medium">{deviceDetail.last_seen_at ? dayjs(deviceDetail.last_seen_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <AppstoreOutlined className="text-xl text-purple-500" />
            <div className="flex-1">
              <Text className="text-gray-500 text-sm block">APK版本</Text>
              <Text className="text-base font-medium">{deviceDetail.apkVersion || '-'}</Text>
            </div>
          </div>

          {/* <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <ClockCircleOutlined className="text-xl text-purple-500" />
            <div className="flex-1">
              <Text className="text-gray-500 text-sm block">创建时间</Text>
              <Text className="text-base font-medium">{deviceDetail.created_at ? dayjs(deviceDetail.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
            </div>
          </div> */}
        </div>
      </div>

      {/* 系统信息 */}
      <div>
        <Title level={5} className="mb-4" style={{ color: '#f0f4f8' }}>系统信息</Title>
        <div className="grid grid-cols-2 gap-4">
          <Card className="text-center border-purple-100 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-purple-100 rounded-full">
              <WifiOutlined className="text-2xl text-purple-500" />
            </div>
            <Text className="text-white-500 text-sm block mb-1" style={{ color: '#f0f4f8' }}>网络类型</Text>
            <Text className="text-base font-semibold" style={{ color: '#f0f4f8' }}>{deviceDetail.network_type}</Text>
          </Card>

          <Card className="text-center border-orange-100 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-orange-100 rounded-full">
              <DatabaseOutlined className="text-2xl text-orange-500" />
            </div>
            <Text className="text-gray-500 text-sm block mb-1" style={{ color: '#f0f4f8' }}>可用存储</Text>
            <Text className="text-base font-semibold" style={{ color: '#f0f4f8' }}>{deviceDetail.storage_available_gb ? deviceDetail.storage_available_gb.toString() : '0'}GB</Text>
          </Card>
        </div>
      </div>

    </div>
  );
};

const DeviceManagement: React.FC = () => {
  const { tableRef, loading, setLoading, dataSource, setDataSource, pagination, updatePagination } = useTable<DeviceListItem>();
  const { openDrawer } = useDrawer();
  const [searchKeyword, setSearchKeyword] = useState('');

  // 获取设备列表
  const fetchDevices = async (page: number = 1, pageSize: number = 10, search: string = '') => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pageSize,
        search,
      };
      
      const response = await devicesAPI.getDevices(params);
      console.log('API返回的设备数据:', response);
      if (!response.list || response.list.length === 0) {
        message.info('暂无设备数据');
        setDataSource([]);
        updatePagination(page, pageSize, 0);
        return;
      }
      
      const devicesWithKey = response.list.map((device) => ({
        ...device,
        key: device.id.toString(),
      }));
      
      setDataSource(devicesWithKey);
      updatePagination(page, pageSize, response.total);
    } catch (error: any) {
      console.error('获取设备列表失败:', error);
      message.error('获取设备列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 列配置
   */
  const columns: TableColumn<DeviceListItem>[] = [
    {
      title: '设备ID',
      dataIndex: 'sn',
      key: 'sn',
      width: 150,
    },
    {
      title: '机型',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 120,
    },
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
      width: 100,
    },
    {
      title: '本地版本',
      dataIndex: 'local_version',
      key: 'local_version',
      width: 100,
    },
    {
      title: '最后检测时间',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 180,
      render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  /**
   * 查看设备详情
   */
  const handleViewDetail = (record: DeviceListItem) => {
    openDrawer({
      title: (
        <div>
          <div className="text-lg font-semibold">设备详情</div>
          <div className="text-sm text-gray-500 font-normal mt-1" style={{color:'#f0f4f8'}}>查看设备的详细信息</div>
        </div>
      ),
      content: <DeviceDetail deviceId={record.id} />,
      width: 600,
    });
  };

  /**
   * 查看升级记录
   */
  const handleViewUpgradeLogs = (record: DeviceListItem) => {
    openDrawer({
      title: (
        <div>
          <div className="text-lg font-semibold">升级记录</div>
          <div className="text-sm text-gray-500 font-normal mt-1" style={{color:'#f0f4f8'}}>查看设备的升级历史记录</div>
        </div>
      ),
      content: <UpgradeLogs deviceId={record.id} deviceSn={record.device_id || record.id.toString()} />,
      width: 800,
    });
  };

  /**
   * 操作列配置
   */
  const actions: TableAction<DeviceListItem>[] = [
    {
      label: <EyeOutlined />,
      key: 'view',
      type: 'text',
      onClick: handleViewDetail,
    },
    {
      label: <HistoryOutlined />,
      key: 'upgrade-logs',
      type: 'text',
      onClick: handleViewUpgradeLogs,
    },
  ];

  /**
   * 处理搜索
   */
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    fetchDevices(1, 10, value);
  };

  /**
   * 处理分页变化
   */
  const handlePaginationChange = (page: number, pageSize: number) => {
    fetchDevices(page, pageSize, searchKeyword);
  };

  return (
    <div className="space-y-6">
      <GlobalTable
        ref={tableRef}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        header={{
          title: '设备列表',
          subtitle: '展示设备上报信息与升级状态',
        }}
        toolbar={{
          showSearch: true,
          searchPlaceholder: '搜索设备ID、机型或地区...',
          onSearch: handleSearch,
          actions: [
            // 机型筛选功能待实现
          ],
        }}
        actions={actions}
        actionsWidth={120}
        pagination={{
          ...pagination,
          total: pagination.total,
          showTotal: true,
          onChange: handlePaginationChange,
        }}
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default DeviceManagement;
